/**
 * Maps a Zuora Payment Pages 2.0 submission/render failure to (a) a recovery
 * lane and (b) the in-dialog banner copy.
 *
 * Routing (see EE-545): request-page (render) failures and synthetic
 * load/timeout failures (`code === null`) are TERMINAL — a fresh
 * `/payment-session` can't fix a bad origin/signature at render or a script
 * that won't load. Every submit-page failure is RE-MINT: a used render
 * token/signature cannot be re-rendered (the token+signature echoed in the
 * callback are response-signed for `/zuora-callback`, not render creds, and
 * Zuora render tokens are attempt-limited), so recovery mints a fresh session
 * and re-renders the form. The `errorCode` only selects the banner copy.
 *
 * Raw `errorMessage` is surfaced to the user ONLY for `BusinessValidationError`
 * (Zuora returns a clean, user-facing sentence there, e.g. "The credit card is
 * expired."). For every other code the raw message is a gateway passthrough
 * with no stable shape ("Transaction declined.402 - [...]") — never shown; a
 * code-keyed generic message is shown instead.
 *
 * `loggable` tells the app layer whether to forward the failure to Datadog.
 * Expected, user-correctable outcomes — business validation, 3DS auth, and
 * interrupted submits (reCaptcha / too-quick) — are `false`: they are user
 * error, not system health signal, and would only add noise. Everything else
 * (gateway, session, system, unknown, and terminal render/load failures) is
 * `true`.
 */
export type ZuoraRecoveryLane = 're-mint' | 'terminal';

export type ZuoraError = {
  /** Zuora `errorCode`, or `null` for a synthetic load/timeout failure. */
  code: string | null;
  title: string;
  body: string;
};

export type ZuoraErrorClassification = {
  lane: ZuoraRecoveryLane;
  error: ZuoraError;
  /** Whether this failure should be forwarded to Datadog. */
  loggable: boolean;
};

export type ClassifyZuoraErrorInput = {
  /** Zuora `responseFrom` — routes render-page vs submit-page failures. */
  responseFrom?: string;
  /** Zuora `errorCode`, or `null` for a synthetic load/timeout failure. */
  code: string | null;
  /** Zuora's raw `errorMessage`; surfaced to the user ONLY for business validation. */
  message?: string;
};

export const RESPONSE_FROM_REQUEST_PAGE = 'Response_From_Request_Page';

const BUSINESS_VALIDATION_CODE = 'BusinessValidationError';
const GATEWAY_CODE = 'GatewayTransactionError';
const THREE_DS_CODE = 'ThreeDs2_Authentication_Exception';
const INTERRUPTED_CODES = new Set(['ReCaptcha_Validation_Failed', 'Submit_Too_Quick']);
const SESSION_CODES = new Set(['Invalid_Security', 'Attempt_Exceed_Limitation']);
const SYSTEM_CODES = new Set([
  'GeneralSystemError',
  'Decryption_Error',
  'Invalid_PaymentGateway',
  'Invalid_Request_Method',
  'Validate_Dynamic_Params_Failed',
]);

const PAYMENT_NOT_SAVED_TITLE = 'Payment Method Not Saved';
const SYSTEM_TITLE = 'Something Went Wrong';

const RENDER_FAILURE_BODY =
  "We couldn't load the payment form. Please close this window and try again.";
const BUSINESS_FALLBACK_BODY = 'Please check your payment details and try again.';
const GATEWAY_BODY = 'Your payment was declined. Please try a different card or bank account.';
const THREE_DS_BODY = "We couldn't verify your card. Please try again.";
const INTERRUPTED_BODY = 'Something interrupted the submission. Please try again.';
const SESSION_BODY = 'Your payment session expired. Please re-enter your details.';
const SYSTEM_BODY = 'Something went wrong. Please try again or contact support.';
const GENERIC_SUBMIT_BODY = "Your payment method update couldn't be completed. Please try again.";

export const classifyZuoraError = ({
  responseFrom,
  code,
  message,
}: ClassifyZuoraErrorInput): ZuoraErrorClassification => {
  // Render/request-page failure — a fresh session cannot help.
  if (responseFrom === RESPONSE_FROM_REQUEST_PAGE) {
    return {
      lane: 'terminal',
      error: { code, title: SYSTEM_TITLE, body: RENDER_FAILURE_BODY },
      loggable: true,
    };
  }
  // Synthetic load-failure / render-timeout (no submit response).
  if (code === null) {
    return {
      lane: 'terminal',
      error: { code, title: SYSTEM_TITLE, body: RENDER_FAILURE_BODY },
      loggable: true,
    };
  }

  // Submit-page matrix — every code re-mints; the code only selects the copy.
  // Expected, user-correctable outcomes (business validation, 3DS, interrupted)
  // are not logged; the rest are.
  if (code === BUSINESS_VALIDATION_CODE) {
    const body = message && message.trim() ? message.trim() : BUSINESS_FALLBACK_BODY;
    return {
      lane: 're-mint',
      error: { code, title: PAYMENT_NOT_SAVED_TITLE, body },
      loggable: false,
    };
  }
  if (code === GATEWAY_CODE) {
    return {
      lane: 're-mint',
      error: { code, title: PAYMENT_NOT_SAVED_TITLE, body: GATEWAY_BODY },
      loggable: true,
    };
  }
  if (code === THREE_DS_CODE) {
    return {
      lane: 're-mint',
      error: { code, title: PAYMENT_NOT_SAVED_TITLE, body: THREE_DS_BODY },
      loggable: false,
    };
  }
  if (INTERRUPTED_CODES.has(code)) {
    return {
      lane: 're-mint',
      error: { code, title: PAYMENT_NOT_SAVED_TITLE, body: INTERRUPTED_BODY },
      loggable: false,
    };
  }
  if (SESSION_CODES.has(code)) {
    return {
      lane: 're-mint',
      error: { code, title: PAYMENT_NOT_SAVED_TITLE, body: SESSION_BODY },
      loggable: true,
    };
  }
  if (SYSTEM_CODES.has(code)) {
    return {
      lane: 're-mint',
      error: { code, title: SYSTEM_TITLE, body: SYSTEM_BODY },
      loggable: true,
    };
  }
  // Unknown submit-page code: re-mint with generic copy.
  return {
    lane: 're-mint',
    error: { code, title: PAYMENT_NOT_SAVED_TITLE, body: GENERIC_SUBMIT_BODY },
    loggable: true,
  };
};
